import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bClaim, B2bClaimAttachment, B2bClaimBudget } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-claims',
  templateUrl: './b2b-claims.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-claims.component.css']
})
export class B2bClaimsComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bClaim[] = [];
  isLoading = false;

  readonly statusOptions = [
    { value: 'Open', label: 'Aberto' },
    { value: 'InAnalysis', label: 'Em análise' },
    { value: 'Approved', label: 'Aprovado' },
    { value: 'Closed', label: 'Fechado' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  columns = [
    { titulo: 'Data', label: 'claimDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Condutor', label: 'driverName' },
    { titulo: 'UF', label: 'state' },
    { titulo: 'Orçamentos', label: 'budgetsLabel' },
    { titulo: 'Status', label: 'status', formatter: (v: any) => this.statusLabel(v) },
    { titulo: 'Descrição', label: 'description' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Imprimir', icon: 'fas fa-print', action: 'print', class: 'btn-view' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bClaims(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'sinistros', 'new']);
  }

  openView(item: B2bClaim): void {
    if (!this.contractId || !item.id) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'sinistros', item.id, 'view']);
  }

  openEdit(item: B2bClaim): void {
    if (!this.contractId || !item.id) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'sinistros', item.id, 'edit']);
  }

  onActionClick(event: { action: string; item: B2bClaim }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'print') this.printClaim(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  printClaim(item: B2bClaim): void {
    if (!this.contractId || !item.id) return;
    this.contractsService.getB2bClaimById(this.contractId, item.id).subscribe({
      next: (full) => this.openPrintWindow(this.normalize(full)),
      error: () => this.openPrintWindow(item)
    });
  }

  private openPrintWindow(claim: B2bClaim): void {
    const winner = (claim.budgets || []).find(b => b.isWinner);
    const budgetsHtml = (claim.budgets || []).map(b => `
      <tr>
        <td>${this.escape(b.workshopName)}${b.isWinner ? ' <strong>(Vencedor)</strong>' : ''}</td>
        <td>${this.escape(b.workshopDocument || '—')}</td>
        <td>${this.formatMoney(b.amount)}</td>
        <td>${this.escape(b.notes || '—')}</td>
      </tr>
    `).join('') || '<tr><td colspan="4">Nenhum orçamento</td></tr>';

    const claimAtts = (claim.attachments || []).map(a =>
      `<li><a href="${this.escape(a.blobUrl)}" target="_blank">${this.escape(a.fileName || a.blobUrl)}</a></li>`
    ).join('') || '<li>Nenhum</li>';

    const winnerAtts = (winner?.attachments || []).map(a =>
      `<li><a href="${this.escape(a.blobUrl)}" target="_blank">${this.escape(a.fileName || a.blobUrl)}</a></li>`
    ).join('') || '<li>Nenhum</li>';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sinistro ${claim.id}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#222;margin:24px}
        h1{font-size:20px;margin:0 0 8px} h2{font-size:16px;margin:24px 0 8px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:13px}
        th{background:#f5f5f5} .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;font-size:13px}
        .meta div span{display:block;color:#666;font-size:11px;text-transform:uppercase}
        @media print{button{display:none}}
      </style></head><body>
      <button onclick="window.print()">Imprimir</button>
      <h1>Sinistro B2B #${claim.id}</h1>
      <p>Contrato ${this.escape(this.contractNumber)} — ${this.escape(this.contractName)}</p>
      <div class="meta">
        <div><span>Placa</span><strong>${this.escape(claim.plate || '—')}</strong></div>
        <div><span>Condutor</span><strong>${this.escape(claim.driverName || '—')}</strong></div>
        <div><span>Data</span><strong>${this.formatDate(claim.claimDate)}</strong></div>
        <div><span>UF</span><strong>${this.escape(claim.state || '—')}</strong></div>
        <div><span>Status</span><strong>${this.statusLabel(claim.status || '')}</strong></div>
        <div><span>Agenda</span><strong>${this.escape(claim.agendaLocation || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Endereço agenda</span><strong>${this.escape(claim.agendaAddress || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Descrição</span><strong>${this.escape(claim.description || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Observações</span><strong>${this.escape(claim.notes || '—')}</strong></div>
      </div>
      <h2>Orçamentos</h2>
      <table><thead><tr><th>Oficina</th><th>CNPJ/CPF</th><th>Valor</th><th>Notas</th></tr></thead>
      <tbody>${budgetsHtml}</tbody></table>
      <h2>Orçamento vencedor</h2>
      ${winner
        ? `<p><strong>${this.escape(winner.workshopName)}</strong> — ${this.escape(winner.workshopDocument || '—')} — ${this.formatMoney(winner.amount)}</p>
           <p>${this.escape(winner.notes || '')}</p>
           <ul>${winnerAtts}</ul>`
        : '<p><em>Nenhum orçamento vencedor selecionado ainda.</em></p>'}
      <h2>Anexos do sinistro (avarias)</h2>
      <ul>${claimAtts}</ul>
      <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
      </body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      this.errorService.showErrorMessage('Permita pop-ups para imprimir.');
      return;
    }
    w.document.write(html);
    w.document.close();
  }

  deleteItem(item: B2bClaim): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.plate || item.description || 'este sinistro', 'o sinistro').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bClaim(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Sinistro excluído.');
            this.loadItems();
          }
        });
      }
    });
  }

  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-sinistros-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Sinistros'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private budgetsSummary(row?: B2bClaim): string {
    if (!row) return '—';
    const budgets = row.budgets || [];
    if (budgets.length === 0) {
      return row.budgetAmount != null ? this.formatMoney(row.budgetAmount) : '—';
    }
    const winner = budgets.find(b => b.isWinner);
    if (winner) return `${budgets.length} · vencedor ${this.formatMoney(winner.amount)}`;
    return `${budgets.length} orçamento(s)`;
  }

  private normalize(dto: any): B2bClaim {
    const budgetsRaw = dto.budgets ?? dto.Budgets ?? [];
    const attachmentsRaw = dto.attachments ?? dto.Attachments ?? [];
    const claim: B2bClaim = {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      b2bContractDriverId: dto.b2bContractDriverId ?? dto.b2BContractDriverId ?? dto.B2BContractDriverId ?? null,
      plate: dto.plate ?? dto.Plate ?? null,
      driverName: dto.driverName ?? dto.DriverName ?? null,
      claimDate: dto.claimDate ?? dto.ClaimDate ?? null,
      state: dto.state ?? dto.State ?? null,
      budgetAmount: dto.budgetAmount ?? dto.BudgetAmount ?? null,
      description: dto.description ?? dto.Description ?? null,
      status: dto.status ?? dto.Status ?? 'Open',
      notes: dto.notes ?? dto.Notes ?? null,
      agendaLocation: dto.agendaLocation ?? dto.AgendaLocation ?? null,
      agendaAddress: dto.agendaAddress ?? dto.AgendaAddress ?? null,
      budgets: Array.isArray(budgetsRaw) ? budgetsRaw.map((b: any) => this.normalizeBudget(b)) : [],
      attachments: Array.isArray(attachmentsRaw)
        ? attachmentsRaw
            .map((a: any) => this.normalizeAttachment(a))
            .filter((a: B2bClaimAttachment) => a.b2bClaimBudgetId == null)
        : []
    };
    claim.budgetsLabel = this.budgetsSummary(claim);
    return claim;
  }

  private normalizeBudget(b: any): B2bClaimBudget {
    const atts = b.attachments ?? b.Attachments ?? [];
    return {
      id: b.id ?? b.Id,
      b2bClaimId: b.b2bClaimId ?? b.B2BClaimId,
      sortOrder: b.sortOrder ?? b.SortOrder ?? 0,
      workshopName: b.workshopName ?? b.WorkshopName ?? '',
      workshopDocument: b.workshopDocument ?? b.WorkshopDocument ?? null,
      amount: Number(b.amount ?? b.Amount ?? 0),
      notes: b.notes ?? b.Notes ?? null,
      isWinner: !!(b.isWinner ?? b.IsWinner),
      attachments: Array.isArray(atts) ? atts.map((a: any) => this.normalizeAttachment(a)) : []
    };
  }

  private normalizeAttachment(a: any): B2bClaimAttachment {
    return {
      id: a.id ?? a.Id,
      b2bClaimId: a.b2bClaimId ?? a.B2BClaimId,
      b2bClaimBudgetId: a.b2bClaimBudgetId ?? a.B2BClaimBudgetId ?? null,
      blobUrl: a.blobUrl ?? a.BlobUrl ?? '',
      fileName: a.fileName ?? a.FileName ?? null,
      contentType: a.contentType ?? a.ContentType ?? null,
      fileSize: a.fileSize ?? a.FileSize ?? null,
      uploadedAt: a.uploadedAt ?? a.UploadedAt ?? null
    };
  }

  private escape(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  statusLabel(value: string): string {
    return this.statusOptions.find(s => s.value === value)?.label || value || '—';
  }
}
